/**
 * usePinnedNav — закреплённые разделы сайдбара.
 *
 * Синхронизирует storage-ключ с текущим юзером (пины у каждого свои)
 * и отдаёт список + toggle. Лимит `MAX_PINNED` — при достижении
 * `canPin` = false, UI показывает подсказку вместо молчаливого отказа.
 */

import {useEffect} from 'react';
import {useAtomValue, useSetAtom} from 'jotai';

import {
  MAX_PINNED,
  type PinnedKey,
  pinnedNavAtom,
  pinnedUserIdAtom,
  togglePinnedAtom,
} from '@atoms/pinnedNav';

import {useAuth} from './useAuth';

type UsePinnedNav = {
  pinned: readonly PinnedKey[];
  isPinned: (key: PinnedKey) => boolean;
  toggle: (key: PinnedKey) => void;
  /** Достигнут лимит — новые пины не влезут (существующие снять можно). */
  isFull: boolean;
};

export const usePinnedNav = (): UsePinnedNav => {
  const {user} = useAuth();
  const setUserId = useSetAtom(pinnedUserIdAtom);
  const pinned = useAtomValue(pinnedNavAtom);
  const toggle = useSetAtom(togglePinnedAtom);

  // Переключили контекст/юзера — переезжаем на его ключ в storage.
  useEffect(() => {
    setUserId(user?.id ?? null);
  }, [user?.id, setUserId]);

  return {
    pinned,
    isPinned: (key) => pinned.includes(key),
    toggle,
    isFull: pinned.length >= MAX_PINNED,
  };
};
